import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarRange, Plus, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SchoolYears() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/school-years"); setRows(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isActive = (y) => !!(y.is_active || y.active);

  const activate = async (y) => {
    if (isActive(y)) return;
    if (!window.confirm(`${t("setAsActive")} : ${y.name} ?\n${t("oneActiveYearHint")}`)) return;
    try { await api.post(`/school/school-years/${y.id}/activate`); toast.success(t("yearActivated")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!form.name) { toast.error(t("name")); return; }
    setBusy(true);
    try {
      await api.post("/school-years", { ...form, status: "Brouillon" });
      toast.success(t("save")); setOpen(false); setForm({ name: "", start_date: "", end_date: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="school-years-page">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2"><CalendarRange className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-display font-bold text-slate-900">{t("schoolYears")}</h1></div>
        {canManage && <Button className="gap-2 rounded-full" data-testid="sy-add-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("newSchoolYear")}</Button>}
      </div>
      <p className="text-sm text-slate-500 mb-5">{t("oneActiveYearHint")}</p>
      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("name")}</TableHead><TableHead>{t("syStart")}</TableHead><TableHead>{t("syEnd")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">{t("noData")}</TableCell></TableRow>
                : rows.map((y) => (
                  <TableRow key={y.id} data-testid="sy-row">
                    <TableCell className="font-medium">{y.name}</TableCell>
                    <TableCell className="text-slate-500">{(y.start_date || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell className="text-slate-500">{(y.end_date || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell>{isActive(y)
                      ? <Badge className="border-0 bg-emerald-100 text-emerald-700 gap-1" data-testid="sy-active-badge"><CheckCircle2 className="h-3 w-3" />{t("activeYear")}</Badge>
                      : <Badge className="border-0 bg-slate-100 text-slate-500">{y.status || "—"}</Badge>}</TableCell>
                    <TableCell className="text-right">
                      {canManage && !isActive(y) && <Button size="sm" variant="outline" className="rounded-full" data-testid={`sy-activate-${y.id}`} onClick={() => activate(y)}>{t("setAsActive")}</Button>}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sy-dialog">
          <DialogHeader><DialogTitle>{t("newSchoolYear")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-slate-500">{t("name")}</label><Input data-testid="sy-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2026-2027" /></div>
            <div><label className="text-xs text-slate-500">{t("syStart")}</label><Input type="date" data-testid="sy-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">{t("syEnd")}</label><Input type="date" data-testid="sy-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={create} disabled={busy} data-testid="sy-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("create")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
